import { ApiError } from '@ignite/api-client';
import {
  AI_CHAT_ATTACHMENT_MAX_COUNT,
  AiChatAttachmentDescriptorSchema,
  AiChatCompletionRequestSchema,
  AiChatGenerationAcceptedResponseSchema,
  type AiChatAttachmentDescriptor,
  type AiChatCompletionRequest,
  type AiChatCompletionResponse,
  type AiChatGenerationAcceptedResponse,
  type AiChatStoredMessage,
  type UserAiFeatureModels,
  type UserAiModelOption,
} from '@ignite/types';
import { z } from 'zod';

export const CHAT_GENERATION_MAX_FOLLOW_MS = 5 * 60_000 + 30_000;
export const CHAT_STREAM_MAX_RETRIES = 8;
export const CHAT_MAX_OUTPUT_TOKENS = 1_024;
export const CHAT_MESSAGE_MAX_COUNT = 64;

const CHAT_STREAM_RETRY_BASE_MS = 250;
const CHAT_STREAM_RETRY_MAX_DELAY_MS = 5_000;
const CHAT_STATE_VERSION = 1;
const NEW_CONVERSATION_KEY = '__new__';

export type ChatFailureKind =
  | 'insufficient_points'
  | 'payment_risk'
  | 'model_unavailable'
  | 'attachment'
  | 'provider_failed'
  | 'temporary'
  | 'conflict';

export type ChatAttachmentItem = {
  localId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: 'uploading' | 'uploaded' | 'error';
  descriptor: AiChatAttachmentDescriptor | null;
  errorCode: string | null;
  file: File | null;
};

export type ChatComposerState = {
  draft: string;
  modelId: string | null;
  attachments: ChatAttachmentItem[];
  errorCode: string | null;
};

export type ChatRunStatus = 'sending' | 'processing' | 'succeeded' | 'failed';

export type ChatRun = {
  request: AiChatCompletionRequest;
  attachments: AiChatAttachmentDescriptor[];
  accepted: AiChatGenerationAcceptedResponse | null;
  response: AiChatCompletionResponse | null;
  generationId: string | null;
  conversationId: string | null;
  userMessageId: string | null;
  assistantMessageId: string | null;
  status: ChatRunStatus;
  cursor: number;
  streamedText: string;
  errorCode: string | null;
  errorMessage: string | null;
  errorDetails: { required_points?: number; available_points?: number } | null;
  resumeDeadlineAt: number | null;
  recoveryRequired: boolean;
};

export type ChatPersistedState = {
  selectedConversationId: string | null;
  composers: Record<string, ChatComposerState>;
  recentModelId: string | null;
  activeRun: ChatRun | null;
  unreadCount: number;
  lastOutcome: 'idle' | 'success' | 'error';
};

export type ChatAttachmentIssue = {
  code: string;
  filename?: string;
  maxCount?: number;
  maxBytes?: number;
};

const errorKindByCode: Record<string, ChatFailureKind> = {
  'chat.insufficient_points': 'insufficient_points',
  'chat.attempt_funding_failed': 'insufficient_points',
  'chat.payment_risk': 'payment_risk',
  'chat.model_not_allowed': 'model_unavailable',
  'chat.model_unavailable': 'model_unavailable',
  'chat.attachment_model_unsupported': 'attachment',
  'chat.attachment_count_exceeded': 'attachment',
  'chat.attachment_total_size_exceeded': 'attachment',
  'chat.attachment_too_large': 'attachment',
  'chat.attachment_expired': 'attachment',
  'chat.attachment_not_uploaded': 'attachment',
  'chat.provider_failed': 'provider_failed',
  'chat.attempt_audit_failed': 'provider_failed',
  'chat.idempotency_conflict': 'conflict',
};

const PersistedAttachmentSchema = z
  .object({
    localId: z.string().min(1).max(160),
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(160),
    sizeBytes: z.number().int().positive(),
    status: z.enum(['uploaded', 'error']),
    descriptor: AiChatAttachmentDescriptorSchema.nullable(),
    errorCode: z.string().min(1).max(160).nullable(),
  })
  .strict();

const PersistedComposerSchema = z
  .object({
    draft: z.string().max(32_000),
    modelId: z.string().min(1).max(160).nullable(),
    attachments: z.array(PersistedAttachmentSchema).max(AI_CHAT_ATTACHMENT_MAX_COUNT),
    errorCode: z.string().min(1).max(160).nullable(),
  })
  .strict();

const PersistedRunSchema = z
  .object({
    request: AiChatCompletionRequestSchema,
    attachments: z.array(AiChatAttachmentDescriptorSchema).max(AI_CHAT_ATTACHMENT_MAX_COUNT),
    accepted: AiChatGenerationAcceptedResponseSchema.nullable(),
    generationId: z.string().min(1).max(128).nullable(),
    conversationId: z.string().min(1).max(128).nullable(),
    userMessageId: z.string().min(1).max(128).nullable(),
    assistantMessageId: z.string().min(1).max(128).nullable(),
    status: z.enum(['sending', 'processing', 'succeeded', 'failed']),
    cursor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    streamedText: z.string().max(32_000),
    errorCode: z.string().min(1).max(160).nullable(),
    errorMessage: z.string().max(1_000).nullable(),
    errorDetails: z
      .object({
        required_points: z.number().int().nonnegative().optional(),
        available_points: z.number().int().nonnegative().optional(),
      })
      .strict()
      .nullable(),
    resumeDeadlineAt: z.number().int().positive().nullable(),
    recoveryRequired: z.boolean(),
  })
  .strict();

const PersistedStateSchema = z
  .object({
    version: z.literal(CHAT_STATE_VERSION),
    selectedConversationId: z.string().min(1).max(128).nullable(),
    composers: z.record(z.string(), PersistedComposerSchema),
    recentModelId: z.string().min(1).max(160).nullable(),
    activeRun: PersistedRunSchema.nullable(),
    unreadCount: z.number().int().nonnegative().max(999),
    lastOutcome: z.enum(['idle', 'success', 'error']),
  })
  .strict();

export function createEmptyChatState(): ChatPersistedState {
  return {
    selectedConversationId: null,
    composers: { [NEW_CONVERSATION_KEY]: createEmptyComposer(null) },
    recentModelId: null,
    activeRun: null,
    unreadCount: 0,
    lastOutcome: 'idle',
  };
}

export function createEmptyComposer(modelId: string | null): ChatComposerState {
  return { draft: '', modelId, attachments: [], errorCode: null };
}

export function chatComposerKey(conversationId: string | null): string {
  return conversationId ?? NEW_CONVERSATION_KEY;
}

export function readPersistedChatState(ownerKey: string): ChatPersistedState {
  if (typeof window === 'undefined') return createEmptyChatState();
  try {
    const raw = window.sessionStorage.getItem(chatStorageKey(ownerKey));
    if (!raw) return createEmptyChatState();
    const parsed = PersistedStateSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return createEmptyChatState();
    return {
      selectedConversationId: parsed.data.selectedConversationId,
      composers: Object.fromEntries(
        Object.entries(parsed.data.composers).map(([key, composer]) => [
          key,
          {
            ...composer,
            attachments: composer.attachments.map((attachment) => ({
              ...attachment,
              file: null,
            })),
          },
        ]),
      ),
      recentModelId: parsed.data.recentModelId,
      activeRun: parsed.data.activeRun ? { ...parsed.data.activeRun, response: null } : null,
      unreadCount: parsed.data.unreadCount,
      lastOutcome: parsed.data.lastOutcome,
    };
  } catch {
    return createEmptyChatState();
  }
}

export function persistChatState(ownerKey: string, state: ChatPersistedState): void {
  if (typeof window === 'undefined') return;
  const composers = Object.fromEntries(
    Object.entries(state.composers).map(([key, composer]) => [
      key,
      {
        ...composer,
        attachments: composer.attachments.map((attachment) => ({
          localId: attachment.localId,
          filename: attachment.filename,
          mimeType: attachment.mimeType,
          sizeBytes: attachment.sizeBytes,
          status: attachment.status === 'uploaded' ? 'uploaded' : 'error',
          descriptor: attachment.descriptor,
          errorCode:
            attachment.status === 'uploading'
              ? 'chat.attachment_upload_interrupted'
              : attachment.errorCode,
        })),
      },
    ]),
  );
  const payload = {
    version: CHAT_STATE_VERSION,
    selectedConversationId: state.selectedConversationId,
    composers,
    recentModelId: state.recentModelId,
    activeRun: state.activeRun
      ? Object.fromEntries(Object.entries(state.activeRun).filter(([key]) => key !== 'response'))
      : null,
    unreadCount: state.unreadCount,
    lastOutcome: state.lastOutcome,
  };
  try {
    window.sessionStorage.setItem(chatStorageKey(ownerKey), JSON.stringify(payload));
  } catch {
    // 浏览器禁用或耗尽会话存储时，当前挂载周期仍保持完整状态。
  }
}

export function clearPersistedChatState(ownerKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(chatStorageKey(ownerKey));
  } catch {
    // 会话存储不可用时无需额外处理。
  }
}

function chatStorageKey(ownerKey: string): string {
  return `ignite.portal.chat.${ownerKey}.v${CHAT_STATE_VERSION}`;
}

export function createClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function chatFailureKind(code: string | null | undefined): ChatFailureKind {
  return code ? (errorKindByCode[code] ?? 'temporary') : 'temporary';
}

export type ChatStreamErrorDisposition = 'cursor_conflict' | 'permanent' | 'retry';

export function chatStreamErrorDisposition(error: unknown): ChatStreamErrorDisposition {
  if (!(error instanceof ApiError)) return 'retry';
  if (error.code === 'chat.cursor_conflict' && error.status === 409) return 'cursor_conflict';
  if (error.status === 408 || error.status === 429 || error.status >= 500) return 'retry';
  if (error.status >= 400 && error.status < 500) return 'permanent';
  return 'retry';
}

export function chatStreamRetryDelayMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  const exponent = Math.max(0, Math.min(20, attempt - 1));
  const capped = Math.min(
    CHAT_STREAM_RETRY_MAX_DELAY_MS,
    CHAT_STREAM_RETRY_BASE_MS * 2 ** exponent,
  );
  const jitter = 0.75 + Math.min(1, Math.max(0, random())) * 0.5;
  return Math.max(1, Math.min(CHAT_STREAM_RETRY_MAX_DELAY_MS, Math.floor(capped * jitter)));
}

export function resolveChatModelId(
  feature: UserAiFeatureModels | undefined,
  options: {
    conversationDefaultModelId?: string | null;
    composerModelId?: string | null;
    recentModelId?: string | null;
  },
): string | null {
  if (!feature || feature.models.length === 0) return null;
  const allowed = new Set(feature.models.map((model) => model.id));
  for (const candidate of [
    options.composerModelId,
    options.conversationDefaultModelId,
    options.recentModelId,
    feature.tenant_default_model_id,
    feature.recommended_model_id,
    feature.models[0]?.id,
  ]) {
    if (candidate && allowed.has(candidate)) return candidate;
  }
  return null;
}

export function findChatModel(
  feature: UserAiFeatureModels | undefined,
  modelId: string | null,
): UserAiModelOption | null {
  return feature?.models.find((model) => model.id === modelId) ?? null;
}

export function chatAttachmentIssue(
  attachments: readonly ChatAttachmentItem[],
  model: UserAiModelOption | null,
): ChatAttachmentIssue | null {
  const failed = attachments.find((attachment) => attachment.status === 'error');
  if (failed)
    return { code: failed.errorCode ?? 'chat.attachment_upload_failed', filename: failed.filename };
  const uploading = attachments.find((attachment) => attachment.status === 'uploading');
  if (uploading) return { code: 'chat.attachment_uploading', filename: uploading.filename };
  if (!model) return attachments.length > 0 ? { code: 'chat.model_unavailable' } : null;

  const capabilities = model.supports.chat_attachments;
  if (attachments.length > capabilities.max_count) {
    return { code: 'chat.attachment_count_exceeded', maxCount: capabilities.max_count };
  }
  const descriptors = attachments.flatMap((attachment) =>
    attachment.descriptor ? [attachment.descriptor] : [],
  );
  const totalBytes = descriptors.reduce((total, attachment) => total + attachment.size_bytes, 0);
  if (totalBytes > capabilities.max_total_bytes) {
    return { code: 'chat.attachment_total_size_exceeded', maxBytes: capabilities.max_total_bytes };
  }
  for (const attachment of descriptors) {
    const capability = capabilities.kinds[attachment.kind];
    if (!capability.supported) {
      return { code: 'chat.attachment_model_unsupported', filename: attachment.filename };
    }
    if (attachment.size_bytes > capability.max_bytes) {
      return {
        code: 'chat.attachment_model_size_exceeded',
        filename: attachment.filename,
        maxBytes: capability.max_bytes,
      };
    }
    if (
      capability.max_duration_ms !== null &&
      attachment.duration_ms !== undefined &&
      attachment.duration_ms > capability.max_duration_ms
    ) {
      return { code: 'chat.attachment_model_duration_exceeded', filename: attachment.filename };
    }
  }
  return null;
}

export function buildChatRequestMessages(
  history: readonly AiChatStoredMessage[],
  draft: string,
  attachmentIds: readonly string[],
): AiChatCompletionRequest['messages'] {
  const context = selectChatRequestHistory(history);
  return [
    ...context,
    {
      role: 'user',
      content: draft,
      ...(attachmentIds.length > 0 ? { attachment_ids: [...attachmentIds] } : {}),
    },
  ];
}

export function selectChatRequestHistory(
  history: readonly AiChatStoredMessage[],
): AiChatCompletionRequest['messages'] {
  return [...history]
    .sort(
      (left, right) =>
        left.created_at.localeCompare(right.created_at) || left.id.localeCompare(right.id),
    )
    .flatMap<AiChatCompletionRequest['messages'][number]>((message) => {
      if (!message.content?.trim()) return [];
      if (message.role === 'assistant' && message.status !== 'succeeded') return [];
      return [{ role: message.role, content: message.content }];
    })
    .slice(-(CHAT_MESSAGE_MAX_COUNT - 1));
}

export function formatBytes(bytes: number, locale: string): string {
  if (bytes < 1_024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1_024;
  let unit = units[0]!;
  for (let index = 1; index < units.length && value >= 1_024; index += 1) {
    value /= 1_024;
    unit = units[index]!;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${unit}`;
}

export function apiErrorInfo(
  error: unknown,
): Pick<ChatRun, 'errorCode' | 'errorMessage' | 'errorDetails'> {
  if (error instanceof ApiError) {
    return {
      errorCode: error.code,
      errorMessage: error.message,
      errorDetails: error.details
        ? {
            ...(error.details.required_points === undefined
              ? {}
              : { required_points: error.details.required_points }),
            ...(error.details.available_points === undefined
              ? {}
              : { available_points: error.details.available_points }),
          }
        : null,
    };
  }
  return {
    errorCode: 'chat.network_error',
    errorMessage: error instanceof Error ? error.message : null,
    errorDetails: null,
  };
}

export function isDeterministicAdmissionError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 409 &&
    error.status !== 429
  );
}
